'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from 'cn'
import { StageLines } from './service-stages'
import type { ServiceStage, VaultDiagramLabels } from '@/lib/services'
import { darkTwin } from '@/lib/theme'

/** Overview (everything lit), then one state per step. */
const LAYERS = [
  '/site/assets/services/vault-diagram.svg',
  '/site/assets/services/vault-diagram-step-1.svg',
  '/site/assets/services/vault-diagram-step-2.svg',
  '/site/assets/services/vault-diagram-step-3.svg',
]

/** Label anchors in the SVG's 1086 × 664 coordinate space. */
const SOURCE_POINTS = [152, 262, 372, 482, 592].map((y) => [90, y])
const OUTPUT_POINTS = [210, 332, 454].map((y) => [958, y])
const VAULT_POINT = [543, 104]
const NOTE_POINT = [543, 526]

function at([x, y]: number[]) {
  return { left: `${(x / 1086) * 100}%`, top: `${(y / 664) * 100}%` }
}

/**
 * The KAI Vault diagram: sources → Vault (unified records, permissions) →
 * agents, copilots, reports. `state` 0 lights everything; 1–3 light the
 * part each step owns. Node labels are HTML over the SVG so they translate.
 */
export function VaultDiagram({
  state,
  labels,
  className,
}: {
  state: number
  labels: VaultDiagramLabels
  className?: string
}) {
  return (
    <figure
      className={cn('svc-vault-diagram', className)}
      data-state={state}
      role="img"
      aria-label={labels.alt}
    >
      {LAYERS.flatMap((src, index) =>
        [src, darkTwin(src)].map((layer, twin) => (
          <Image
            key={layer}
            className={cn('svc-vault-layer', twin ? 'theme-dark-only' : 'theme-light-only')}
            data-layer={index}
            src={layer}
            alt=""
            fill
            sizes="(max-width: 999px) 100vw, 640px"
          />
        )),
      )}
      {labels.sources.map((name, index) => (
        <span
          key={name}
          className="svc-vault-label"
          data-part="src"
          data-key={index}
          style={at(SOURCE_POINTS[index] ?? SOURCE_POINTS[0])}
        >
          {name}
        </span>
      ))}
      <span
        className="svc-vault-label svc-vault-label--vault"
        data-part="vault"
        style={at(VAULT_POINT)}
      >
        {labels.vault}
      </span>
      <span className="svc-vault-label" data-part="vault" style={at(NOTE_POINT)}>
        {labels.vaultNote}
      </span>
      {labels.outputs.map((name, index) => (
        <span
          key={name}
          className="svc-vault-label svc-vault-label--out"
          data-part="out"
          style={at(OUTPUT_POINTS[index] ?? OUTPUT_POINTS[0])}
        >
          {name}
        </span>
      ))}
    </figure>
  )
}

/**
 * The three Vault steps beside a sticky diagram. The step crossing the
 * middle of the viewport is active and sets the diagram's state. On small
 * screens the diagram shows once (everything lit) and each step carries a
 * small highlighted copy instead.
 */
export function VaultSteps({
  stages,
  diagram,
}: {
  stages: ServiceStage[]
  diagram: VaultDiagramLabels
}) {
  const [active, setActive] = useState(0)
  const steps = useRef<(HTMLLIElement | null)[]>([])
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(Number((entry.target as HTMLElement).dataset.index))
        }
      },
      { rootMargin: '-45% 0px -45% 0px' },
    )
    for (const step of steps.current) if (step) observer.observe(step)
    return () => observer.disconnect()
  }, [])
  return (
    <div className="svc-vault">
      <div className="svc-vault-figure">
        <VaultDiagram state={active + 1} labels={diagram} />
      </div>
      <ol className="svc-vault-steps">
        {stages.map((stage, index) => (
          <li
            key={stage.number}
            ref={(node) => {
              steps.current[index] = node
            }}
            data-index={index}
            className={cn('svc-vault-step', index === active && 'is-active')}
          >
            <div className="svc-vault-step-head">
              {[LAYERS[index + 1] ?? LAYERS[0]].flatMap((src) =>
                [src, darkTwin(src)].map((thumb, twin) => (
                  <Image
                    key={thumb}
                    className={cn('svc-vault-thumb', twin ? 'theme-dark-only' : 'theme-light-only')}
                    src={thumb}
                    alt=""
                    width={1086}
                    height={664}
                    sizes="120px"
                  />
                )),
              )}
              <div>
                <span className="svc-card-index">{stage.number}</span>
                <h3>{stage.title}</h3>
              </div>
            </div>
            {stage.body ? <p>{stage.body}</p> : null}
            <StageLines lines={stage.lines} />
          </li>
        ))}
      </ol>
    </div>
  )
}
